/**
 * Development/local-only store for API error payloads the frontend surfaces.
 * Uses localStorage keyed by local calendar date. No-ops outside local/dev
 * (except settings, which are always readable for Health Settings UI).
 */

const KEY_PREFIX = "moltech:devApiErrors:v1:";
const SETTINGS_KEY = "moltech:systemHealth:v1";
const MAX_PER_DAY = 200;
const RAW_MAX_CHARS = 8000;

const PROD_ENVS = new Set(["production", "preproduction"]);

const DEFAULT_SETTINGS = {
  apiErrorTrackingEnabled: true,
  purgeRetentionDays: 15,
  trackServerErrors: true,
  purgeActivityLogsEnabled: false,
};

export const isDevApiErrorLoggingEnabled = () => {
  const env = String(process.env.REACT_APP_ENVIRONMENT || "")
    .trim()
    .toLowerCase();
  if (!env) return true;
  return !PROD_ENVS.has(env);
};

export const getSystemHealthSettings = () => {
  if (typeof localStorage === "undefined") return { ...DEFAULT_SETTINGS };
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_SETTINGS,
      ...parsed,
      purgeRetentionDays: clampDays(parsed.purgeRetentionDays),
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
};

export const saveSystemHealthSettings = (partial = {}) => {
  const next = {
    ...getSystemHealthSettings(),
    ...partial,
    purgeRetentionDays: clampDays(
      partial.purgeRetentionDays ?? getSystemHealthSettings().purgeRetentionDays
    ),
  };
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  }
  return next;
};

const clampDays = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_SETTINGS.purgeRetentionDays;
  return Math.min(365, Math.max(1, Math.round(n)));
};

const toDateKey = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return toDateKey(new Date());
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const storageKey = (dateKey) => `${KEY_PREFIX}${dateKey}`;

const safeParse = (raw) => {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const readDay = (dateKey) => {
  if (typeof localStorage === "undefined") return [];
  try {
    return safeParse(localStorage.getItem(storageKey(dateKey)) || "[]");
  } catch {
    return [];
  }
};

const writeDay = (dateKey, entries) => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(storageKey(dateKey), JSON.stringify(entries));
  } catch {
    // Quota / private mode — ignore
  }
};

const makeId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `err-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const trimRaw = (value) => {
  if (value == null) return null;
  let text;
  try {
    text = typeof value === "string" ? value : JSON.stringify(value);
  } catch {
    text = String(value);
  }
  if (text.length <= RAW_MAX_CHARS) return text;
  return `${text.slice(0, RAW_MAX_CHARS)}…`;
};

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

export const buildDevApiErrorGroupKey = (entry) => {
  const api = normalizeText(entry?.methodName || entry?.url || "unknown");
  const msg = normalizeText(
    entry?.errorMessage || entry?.message || entry?.innerErrorMessage || ""
  );
  return `${api}|${msg}`;
};

/**
 * Append one captured API error. No-op when not local/dev or tracking disabled.
 */
export const recordDevApiError = (partial = {}) => {
  if (!isDevApiErrorLoggingEnabled()) return null;
  const settings = getSystemHealthSettings();
  if (!settings.apiErrorTrackingEnabled) return null;

  const at = partial.at ? new Date(partial.at) : new Date();
  const dateKey = partial.dateKey || toDateKey(at);
  const entry = {
    id: partial.id || makeId(),
    at: at.toISOString(),
    dateKey,
    url: partial.url || null,
    method: partial.method || null,
    statusCode: partial.statusCode ?? null,
    methodName: partial.methodName || null,
    errorMessage: partial.errorMessage || null,
    innerErrorMessage: partial.innerErrorMessage || null,
    message: partial.message || null,
    raw: trimRaw(partial.raw),
    status: "open",
    solvedAt: null,
    solvedBy: null,
    groupKey: null,
  };
  entry.groupKey = buildDevApiErrorGroupKey(entry);

  const existing = readDay(dateKey);
  const next = [...existing, entry];
  while (next.length > MAX_PER_DAY) next.shift();
  writeDay(dateKey, next);
  return entry;
};

export const listDevApiErrorDates = () => {
  if (typeof localStorage === "undefined") return [];
  const dates = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key?.startsWith(KEY_PREFIX)) continue;
      const dateKey = key.slice(KEY_PREFIX.length);
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) dates.push(dateKey);
    }
  } catch {
    return [];
  }
  return dates.sort((a, b) => b.localeCompare(a));
};

export const getDevApiErrorsByDate = (dateKey) => {
  if (!dateKey) return [];
  return readDay(dateKey)
    .map((e) => ({
      ...e,
      status: e.status === "solved" ? "solved" : "open",
      groupKey: e.groupKey || buildDevApiErrorGroupKey(e),
    }))
    .sort((a, b) => String(b.at).localeCompare(String(a.at)));
};

export const getAllDevApiErrors = () =>
  listDevApiErrorDates().flatMap((dateKey) => getDevApiErrorsByDate(dateKey));

const updateEntryEverywhere = (predicate, updater) => {
  listDevApiErrorDates().forEach((dateKey) => {
    const rows = readDay(dateKey);
    let changed = false;
    const next = rows.map((row) => {
      if (!predicate(row)) return row;
      changed = true;
      return updater(row);
    });
    if (changed) writeDay(dateKey, next);
  });
};

const removeEntriesEverywhere = (predicate) => {
  let removed = 0;
  listDevApiErrorDates().forEach((dateKey) => {
    const rows = readDay(dateKey);
    const next = rows.filter((row) => {
      const keep = !predicate(row);
      if (!keep) removed += 1;
      return keep;
    });
    if (next.length === 0) {
      try {
        localStorage.removeItem(storageKey(dateKey));
      } catch {
        // ignore
      }
    } else if (next.length !== rows.length) {
      writeDay(dateKey, next);
    }
  });
  return removed;
};

export const markDevApiErrorSolved = (id, solvedBy = null) => {
  if (!id) return 0;
  return markDevApiErrorsSolvedByIds([id], solvedBy);
};

export const markDevApiErrorsSolvedByIds = (ids, solvedBy = null) => {
  const idSet = new Set((ids || []).filter(Boolean));
  if (idSet.size === 0) return 0;
  const at = new Date().toISOString();
  let count = 0;
  updateEntryEverywhere(
    (row) => idSet.has(row.id),
    (row) => {
      count += 1;
      return {
        ...row,
        status: "solved",
        solvedAt: at,
        solvedBy: solvedBy || null,
      };
    }
  );
  return count;
};

export const markDevApiErrorGroupSolved = (groupKey, solvedBy = null) => {
  if (!groupKey) return 0;
  const at = new Date().toISOString();
  let count = 0;
  updateEntryEverywhere(
    (row) =>
      (row.groupKey || buildDevApiErrorGroupKey(row)) === groupKey &&
      row.status !== "solved",
    (row) => {
      count += 1;
      return {
        ...row,
        status: "solved",
        solvedAt: at,
        solvedBy: solvedBy || null,
      };
    }
  );
  return count;
};

export const countOpenDevApiErrorsInGroup = (groupKey) => {
  if (!groupKey) return 0;
  return getAllDevApiErrors().filter(
    (row) =>
      (row.groupKey || buildDevApiErrorGroupKey(row)) === groupKey &&
      row.status !== "solved"
  ).length;
};

export const deleteDevApiError = (id) => {
  if (!id) return 0;
  return deleteDevApiErrorsByIds([id]);
};

export const deleteDevApiErrorsByIds = (ids) => {
  const idSet = new Set((ids || []).filter(Boolean));
  if (idSet.size === 0) return 0;
  return removeEntriesEverywhere((row) => idSet.has(row.id));
};

export const deleteDevApiErrorGroup = (groupKey) => {
  if (!groupKey) return 0;
  return removeEntriesEverywhere(
    (row) => (row.groupKey || buildDevApiErrorGroupKey(row)) === groupKey
  );
};

export const purgeDevApiErrorsOlderThanDays = (days) => {
  const retention = clampDays(days ?? getSystemHealthSettings().purgeRetentionDays);
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - retention);
  const cutoffMs = cutoff.getTime();

  return removeEntriesEverywhere((row) => {
    const t = row.at ? new Date(row.at).getTime() : 0;
    return Number.isFinite(t) && t < cutoffMs;
  });
};

export const getGroupedDevApiErrors = () => {
  const map = new Map();
  getAllDevApiErrors().forEach((entry) => {
    const key = entry.groupKey || buildDevApiErrorGroupKey(entry);
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        groupKey: key,
        methodName: entry.methodName || null,
        url: entry.url || null,
        errorMessage: entry.errorMessage || entry.message || null,
        statusCode: entry.statusCode ?? null,
        count: 1,
        openCount: entry.status === "solved" ? 0 : 1,
        solvedCount: entry.status === "solved" ? 1 : 0,
        firstSeen: entry.at,
        lastSeen: entry.at,
        status: entry.status === "solved" ? "solved" : "open",
        entries: [entry],
      });
      return;
    }
    existing.count += 1;
    if (entry.status === "solved") existing.solvedCount += 1;
    else existing.openCount += 1;
    if (String(entry.at) < String(existing.firstSeen)) existing.firstSeen = entry.at;
    if (String(entry.at) > String(existing.lastSeen)) existing.lastSeen = entry.at;
    existing.status = existing.openCount > 0 ? "open" : "solved";
    existing.entries.push(entry);
  });

  return Array.from(map.values()).sort((a, b) =>
    String(b.lastSeen).localeCompare(String(a.lastSeen))
  );
};

export const clearDevApiErrorsByDate = (dateKey) => {
  if (typeof localStorage === "undefined" || !dateKey) return;
  try {
    localStorage.removeItem(storageKey(dateKey));
  } catch {
    // ignore
  }
};

export const clearAllDevApiErrors = () => {
  if (typeof localStorage === "undefined") return;
  listDevApiErrorDates().forEach((dateKey) => clearDevApiErrorsByDate(dateKey));
};

export const todayDevApiErrorDateKey = () => toDateKey(new Date());
